<template>
  <div class="product-list-mobile">
    <div class="page-header">
      <h2>商品管理</h2>
      <button class="btn btn-primary" @click="navigateToCreateProduct">新增商品</button>
    </div>

    <!-- 搜尋和篩選區域 -->
    <div class="filter-section">
      <div class="search-wrapper">
        <input
          type="text"
          placeholder="搜尋商品名稱、SKU或規格"
          v-model="searchText"
          @input="onSearchInput"
          @keyup.enter="fetchProducts"
          class="search-input"
        >
        <button
          v-if="searchText"
          @click="clearSearchText"
          class="clear-btn"
          type="button"
        >
          ×
        </button>
      </div>

      <div class="filter-row">
        <div class="filter-group">
          <label>分類</label>
          <el-cascader
            v-model="selectedCategoryId"
            :options="categoryTreeData"
            :props="{
              checkStrictly: true,
              emitPath: false,
              expandTrigger: 'hover',
              value: 'id',
              label: 'name'
            }"
            @change="onCategoryChange"
            @visible-change="handleCategoryVisibleChange"
            placeholder="選擇分類"
            style="width: 100%"
            clearable
          />
        </div>

        <div class="filter-group">
          <label>狀態</label>
          <select v-model="selectedStatus" @change="onStatusChange" class="status-select">
            <option value="all">全部</option>
            <option value="enabled">僅啟用</option>
            <option value="disabled">僅停用</option>
          </select>
        </div>

        <button class="btn btn-secondary reset-btn" @click="resetFilters">重置</button>
      </div>
    </div>

    <!-- 商品卡片列表 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="products.length === 0" class="empty-container">
      <p>沒有找到商品</p>
    </div>

    <div v-else class="product-grid">
      <div
        v-for="product in products"
        :key="product.id"
        :data-product-id="product.id"
        class="product-card"
        @click="navigateToEdit(product.id)"
      >
        <div class="product-image-wrapper">
          <img
            v-if="product.primary_image_path"
            :src="getImageUrl(product.primary_image_path)"
            :alt="product.name"
            @error="onImageError"
            class="product-image"
          >
          <div v-else class="no-image">無圖片</div>
          <div class="status-badges">
            <div class="status-badge" :class="{ 'active': product.is_listed !== false, 'inactive': product.is_listed === false }">
              {{ product.is_listed === false ? '下架' : '上架' }}
            </div>
            <div class="status-badge" :class="{ 'active': product.is_active, 'inactive': !product.is_active }">
              {{ product.is_active ? '啟用' : '停用' }}
            </div>
          </div>
        </div>

        <div class="product-info">
          <h3 class="product-name">
            {{ product.name || '未設定' }}
          </h3>
          <div class="product-details">
            <div class="detail-item">
              <span class="label">SKU:</span>
              <span class="value">{{ product.sku || '未設定' }}</span>
            </div>
            <div class="detail-item">
              <span class="label">價格:</span>
              <span class="value price">{{ formatCurrency(product.retail_price) }}</span>
            </div>
            <div class="detail-item">
              <span class="label">庫存:</span>
              <span class="value">{{ product.stock_quantity || 0 }}</span>
            </div>
            <div class="detail-item" v-if="getCategoryShortPath(product)">
              <span class="label">分類:</span>
              <span class="value category">{{ getCategoryShortPath(product) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分頁 -->
    <div class="pagination-wrapper">
      <CustomPagination
        v-bind="paginationConfig"
        @change="handlePageChange"
        @show-size-change="handlePageSizeChange"
        class="product-pagination"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import { ElMessage, ElCascader } from 'element-plus'
import { useProductList } from '@/composables/useProductList'
import CustomPagination from '@/components/CustomPagination.vue'

const {
  products,
  loading,
  searchText,
  selectedCategoryId,
  selectedStatus,
  categoryTreeData,
  paginationConfig,
  fetchProducts,
  fetchCategories,
  handlePageChange,
  handlePageSizeChange,
  onSearchInput,
  clearSearchText,
  onCategoryChange,
  onStatusChange,
  handleCategoryVisibleChange,
  resetFilters,
  navigateToCreateProduct,
  navigateToEdit,
  getImageUrl,
  formatCurrency,
  getCategoryShortPath,
  initialize
} = useProductList()

function onImageError(event) {
  event.target.style.display = 'none'
  const parent = event.target.parentElement
  if (parent && !parent.querySelector('.no-image')) {
    const noImageDiv = document.createElement('div')
    noImageDiv.className = 'no-image'
    noImageDiv.textContent = '無圖片'
    parent.appendChild(noImageDiv)
  }
}

onMounted(async () => {
  await initialize()
})
</script>

<style scoped>
.product-list-mobile {
  padding: 16px;
  padding-bottom: 32px;
  background-color: #f4f7fa;
  min-height: 100vh;
  min-height: 100dvh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.page-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.filter-section {
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-wrapper {
  position: relative;
  margin-bottom: 12px;
}

.search-input {
  width: 100%;
  height: 40px;
  padding: 8px 35px 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.search-input:focus {
  border-color: #007bff;
  outline: none;
}

.clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.clear-btn:hover {
  background-color: #f0f0f0;
  color: #666;
}

.filter-row {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.filter-group {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.filter-group label {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.status-select {
  height: 40px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
}

.reset-btn {
  height: 40px;
  padding: 0 16px;
  white-space: nowrap;
}

.loading-container,
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.product-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.product-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: stretch;
}

.product-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.product-card.is-last-viewed {
  box-shadow: 0 0 0 2px #007bff, 0 4px 12px rgba(0, 123, 255, 0.25);
}

.product-image-wrapper {
  position: relative;
  width: 160px;
  min-width: 160px;
  height: auto;
  min-height: 120px;
  background: #f9f9f9;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  color: #999;
  font-size: 12px;
  text-align: center;
}

.status-badges {
  position: absolute;
  top: 4px;
  left: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.status-badge {
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
}

.status-badge.active {
  background: #13ce66;
  color: white;
}

.status-badge.inactive {
  background: #ff4949;
  color: white;
}

.product-info {
  padding: 10px 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
}

.product-name {
  margin: 0 0 6px 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: pointer;
}

.product-name:hover {
  color: #007bff;
}

.product-details {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 8px;
}

.detail-item {
  display: flex;
  align-items: center;
  font-size: 12px;
}

.detail-item .label {
  color: #666;
  margin-right: 4px;
  min-width: 40px;
}

.detail-item .value {
  color: #333;
  flex: 1;
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-item .value.price {
  color: #007bff;
  font-weight: 600;
}

.detail-item .value.category {
  font-size: 11px;
  color: #666;
}

.product-actions {
  display: flex;
  gap: 6px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.action-btn {
  flex: 1;
  padding: 6px 4px;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.edit-btn {
  background: #007bff;
  color: white;
}

.edit-btn:hover {
  background: #0056b3;
}

.copy-btn {
  background: #6c757d;
  color: white;
}

.copy-btn:hover:not(:disabled) {
  background: #5a6268;
}

.copy-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.frontend-btn {
  background: #28a745;
  color: white;
}

.frontend-btn:hover {
  background: #218838;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.pagination-wrapper {
  margin-top: 16px;
}

:deep(.el-cascader) {
  width: 100%;
}

:deep(.el-cascader .el-input__wrapper) {
  height: 40px;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
}

:deep(.el-cascader .el-input__wrapper:hover) {
  border-color: #007bff;
}

:deep(.el-cascader .el-input__wrapper.is-focus) {
  border-color: #007bff;
}

@media (min-width: 480px) {
  .product-grid {
    grid-template-columns: 1fr;
  }

  .product-name {
    font-size: 15px;
  }

  .detail-item {
    font-size: 13px;
  }

  .action-btn {
    font-size: 12px;
    padding: 8px 6px;
  }
}

@media (min-width: 768px) {
  .product-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>

